/**
 * ENCONTRAR A ESCOLA NA BASE OFICIAL — T18.9, regras de 06/10 (repo instituicoes-ensino).
 *
 * O ponto: *"ao invés do cara ter que cadastrar manualmente o nome da instituição, ter uma base que
 * ele possa vincular"*. Esta busca substitui as duas portas antigas de "Minha instituição": a lista
 * só das escolas já ativas, e o formulário de texto livre "a sua escola não está na lista?".
 *
 * Cada resultado diz a situação daquela escola NO HQ-LAB e o que a pessoa pode fazer, pelos casos
 * das regras:
 *   · ativa ............................. pedir para entrar como estudante ou educador (B, C)
 *   · ainda não está / aguardando /
 *     recusada .......................... educador pede (ou reivindica de novo) a ativação ao
 *                                        admin geral (A); estudante pede, e o pedido fica guardado
 *                                        até a escola ativar, com o aviso (D, revisto em 08/10)
 *   · suspensa .......................... ninguém entra
 */
import { useEffect, useRef, useState } from "react";
import { InstituicaoDaBase, pedirAtivacao, situacoesNoHqLab, solicitarVinculo } from "./api";
import { BaseDeEscolas, ROTULO_REDE, carregarBase } from "./base-de-escolas";
import { AVISO_INSTITUICAO_INATIVA, PapelInstituicao, SituacaoInstituicao, Vinculo, podePedirVinculo } from "./modelo";

type Situacao = SituacaoInstituicao | "inexistente";

const ROTULO_SITUACAO: Record<Situacao, string> = {
  ativa: "já está no HQ-Lab",
  solicitada: "aguardando a equipe do HQ-Lab ativar",
  inexistente: "ainda não está no HQ-Lab",
  suspensa: "suspensa no HQ-Lab",
  recusada: "não foi ativada no HQ-Lab",
};

interface Props {
  pessoa: { uid: string; nome?: string; email?: string };
  meusVinculos: Vinculo[];
  ocupado: boolean;
  /** O mesmo envelope de "Minha instituição": trava a tela, mostra o aviso e recarrega. */
  executar: (fn: () => Promise<{ ok: boolean; erro?: string }>, sucesso: string) => Promise<void>;
}

export default function BuscaDeEscola({ pessoa, meusVinculos, ocupado, executar }: Props) {
  const [base, setBase] = useState<BaseDeEscolas | null>(null);
  const [erroBase, setErroBase] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [nivel, setNivel] = useState<"" | "basica" | "superior">("");
  const [papel, setPapel] = useState<PapelInstituicao>("aluno");
  const [resultados, setResultados] = useState<InstituicaoDaBase[]>([]);
  const [situacoes, setSituacoes] = useState<Record<string, Situacao>>({});
  const pedidoAtual = useRef(0);
  // Depois de pedir, a situação da escola muda (ex.: passa a aguardando): relê a lista.
  const [releitura, setReleitura] = useState(0);
  const agir = async (fn: () => Promise<{ ok: boolean; erro?: string }>, msg: string) => {
    await executar(fn, msg);
    setReleitura((n) => n + 1);
  };

  // A base tem 1,9 MB: só é baixada quando a pessoa começa a procurar.
  useEffect(() => {
    if (base || texto.trim().length < 3) return;
    carregarBase()
      .then(setBase)
      .catch((e: unknown) => setErroBase(e instanceof Error ? e.message : String(e)));
  }, [texto, base]);

  useEffect(() => {
    if (!base || texto.trim().length < 3) {
      setResultados([]);
      return;
    }
    const achados = base.buscar(texto, { nivel: nivel || undefined, limite: 12 });
    setResultados(achados);
    const meu = ++pedidoAtual.current;
    void situacoesNoHqLab(achados.map((a) => a.id)).then((s) => {
      if (meu === pedidoAtual.current) setSituacoes(s);
    });
  }, [base, texto, nivel, releitura]);

  const jaTenho = (id: string) => meusVinculos.find((v) => v.instituicaoId === id);

  function acao(i: InstituicaoDaBase) {
    const situacao = situacoes[i.id];
    if (!situacao) return <span className="inst-detalhe">conferindo…</span>;
    const meu = jaTenho(i.id);
    if (meu) return <span className="inst-detalhe">você já tem pedido ou vínculo aqui</span>;

    if (!podePedirVinculo(papel, situacao)) {
      return <span className="inst-detalhe">suspensa: não aceita pedidos agora</span>;
    }

    if (situacao === "ativa" || papel === "aluno") {
      const ativa = situacao === "ativa";
      return (
        <button
          type="button"
          className="botao-secundario"
          disabled={ocupado}
          onClick={() =>
            agir(
              () =>
                solicitarVinculo({
                  instituicaoId: i.id,
                  instituicaoNome: i.nome,
                  uid: pessoa.uid,
                  nome: pessoa.nome,
                  email: pessoa.email,
                  papel,
                }),
              ativa ? "Pedido enviado. Um educador da escola vai receber." : "Pedido guardado. " + AVISO_INSTITUICAO_INATIVA
            )
          }
        >
          Pedir para entrar
        </button>
      );
    }

    // Caso A: educador e escola ainda não ativa (ou recusada, que ele reivindica de novo).
    return (
      <button
        type="button"
        className="botao-secundario"
        disabled={ocupado}
        onClick={() =>
          agir(
            () => pedirAtivacao(i, pessoa),
            "Pedido enviado. A equipe do HQ-Lab confere e ativa a escola com você como educador."
          )
        }
      >
        Pedir a ativação desta escola
      </button>
    );
  }

  return (
    <div className="inst-bloco" data-testid="busca-de-escola">
      <h3>Encontrar a sua escola</h3>
      <p className="ajuda">
        Procure pelo nome ou pela cidade. A lista é a oficial do INEP, com as escolas e faculdades da
        Bahia — ninguém precisa digitar o nome da escola.
      </p>

      <div className="inst-formulario">
        <label className="campo">
          <span>Escola ou cidade</span>
          <input
            id="busca-escola"
            value={texto}
            maxLength={80}
            placeholder="Ex.: Rui Barbosa Salvador"
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="campo">
          <span>Nível</span>
          <select id="busca-nivel" value={nivel} onChange={(e) => setNivel(e.target.value as typeof nivel)}>
            <option value="">Todos</option>
            <option value="basica">Educação básica</option>
            <option value="superior">Ensino superior</option>
          </select>
        </label>
        <label className="campo">
          <span>Você entra como</span>
          <select id="busca-papel" value={papel} onChange={(e) => setPapel(e.target.value as PapelInstituicao)}>
            <option value="aluno">Estudante</option>
            <option value="educador">Educador</option>
          </select>
        </label>
      </div>

      {erroBase && <p className="inst-aviso inst-aviso-erro">{erroBase}</p>}
      {texto.trim().length >= 3 && !base && !erroBase && <p className="ajuda">Carregando a lista oficial…</p>}
      {base && texto.trim().length >= 3 && resultados.length === 0 && (
        <p className="ajuda">Nenhuma instituição com esse nome. Tente só uma parte do nome, ou a cidade.</p>
      )}

      {resultados.length > 0 && (
        <ul className="inst-lista" data-testid="resultados-base">
          {resultados.map((i) => (
            <li key={i.id} className="inst-item" data-id={i.id}>
              <div>
                <strong>{i.nome}</strong>
                <span className="inst-detalhe">
                  {i.municipio} · {i.nivel === "superior" ? "ensino superior" : "educação básica"}
                  {i.rede ? ` · ${ROTULO_REDE[i.rede] ?? i.rede}` : ""}
                  {situacoes[i.id] ? ` · ${ROTULO_SITUACAO[situacoes[i.id]]}` : ""}
                </span>
              </div>
              <span className="inst-acoes">{acao(i)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
